import { useCallback, useEffect, useRef, useState } from "react";
import {
    Modal,
    Form,
    Input,
    message,
    Switch,
    Select,
    Tooltip,
    Button
} from "antd";
import {
    EditOutlined,
    PlusOutlined,
    ThunderboltOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    BulbOutlined,
    InfoCircleOutlined
} from "@ant-design/icons";
import { RequestGeneratePrompt } from "../../Actions/critere.action";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../Actions/user.action";
import { useTranslation } from "react-i18next";
import { buildReviewProfileOptions } from "../../utils/reviewProfiles";
import "./CriterionFormModal.css";

const cleanText = (value) => (typeof value === "string" ? value.trim() : value);

const normalizeLanguage = (language) => (
    String(language || "").toLowerCase().startsWith("fr") ? "fr" : "en"
);

const compactTranslation = (label, description) => {
    const translation = {};
    if (label) translation.label = label;
    if (description) translation.description = description;
    return Object.keys(translation).length ? translation : null;
};

const hasTranslationForLocale = (translations = {}, locale) => Boolean(
    translations?.[locale]?.label || translations?.[locale]?.description
);

const buildLocalizedFormValues = (values = {}, primaryLocale = "fr") => {
    const translations = values.translations || {};
    const hasTranslations = Boolean(translations.fr || translations.en);

    return {
        ...values,
        title_fr: translations.fr?.label || (!hasTranslations && primaryLocale === "fr" ? values.title : ""),
        description_fr: translations.fr?.description || (!hasTranslations && primaryLocale === "fr" ? values.description : ""),
        title_en: translations.en?.label || (!hasTranslations && primaryLocale === "en" ? values.title : ""),
        description_en: translations.en?.description || (!hasTranslations && primaryLocale === "en" ? values.description : ""),
    };
};

const buildCriterionPayload = (values, initialValues) => {
    const {
        title_fr,
        description_fr,
        title_en,
        description_en,
        translations: _translations,
        ...payload
    } = values;

    const frLabel = cleanText(title_fr);
    const frDescription = cleanText(description_fr);
    const enLabel = cleanText(title_en);
    const enDescription = cleanText(description_en);
    const translations = {};
    const fr = compactTranslation(frLabel, frDescription);
    const en = compactTranslation(enLabel, enDescription);

    if (fr) translations.fr = fr;
    if (en) translations.en = en;

    payload.title = frLabel || enLabel || cleanText(initialValues?.title) || "";
    payload.description = frDescription || enDescription || cleanText(initialValues?.description) || null;
    payload.translations = Object.keys(translations).length ? translations : null;

    return payload;
};

const CriterionFormModal = ({
    open,
    onClose,
    onSubmit,
    initialValues,
    existingSections,
    canEditPrompt = true,
    canGeneratePrompt = canEditPrompt,
}) => {
    const { t, i18n } = useTranslation("criteriaView");
    const [form] = Form.useForm();
    const uniqueSections = [...new Set(existingSections)];
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.users?.currentUser || state.users?.user || {});
    const watchedVisibilityScope = Form.useWatch("visibility_scope", form);
    const primaryLocale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const secondaryLocale = primaryLocale === "fr" ? "en" : "fr";
    const primaryTitleName = `title_${primaryLocale}`;
    const primaryDescriptionName = `description_${primaryLocale}`;
    const secondaryTitleName = `title_${secondaryLocale}`;
    const secondaryDescriptionName = `description_${secondaryLocale}`;
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [acceptDoc, setAcceptDoc] = useState(false);
    const [showSecondaryLocale, setShowSecondaryLocale] = useState(false);
    const [promptCooldownUntil, setPromptCooldownUntil] = useState(0);
    const generationInFlightRef = useRef(false);
    const cooldownTimerRef = useRef(null);

    const clearPromptCooldownTimer = useCallback(() => {
        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current);
            cooldownTimerRef.current = null;
        }
    }, []);

    const startPromptCooldown = useCallback(() => {
        const cooldownUntil = Date.now() + 10000;
        clearPromptCooldownTimer();
        setPromptCooldownUntil(cooldownUntil);
        cooldownTimerRef.current = setTimeout(() => {
            setPromptCooldownUntil(0);
            cooldownTimerRef.current = null;
        }, 10000);
    }, [clearPromptCooldownTimer]);

    useEffect(() => {
        if (!open) {
            form.resetFields();
            setIsGenerating(false);
            generationInFlightRef.current = false;
            clearPromptCooldownTimer();
            setPromptCooldownUntil(0);
            setIsSaving(false);
            setIsActive(true);
            setAcceptDoc(false);
            setShowSecondaryLocale(false);
        } else if (initialValues) {
            form.setFieldsValue(buildLocalizedFormValues(initialValues, primaryLocale));
            setIsActive(initialValues.is_active ?? true);
            setAcceptDoc(initialValues.accept_document ?? false);
            setShowSecondaryLocale(hasTranslationForLocale(initialValues.translations, secondaryLocale));
        } else {
            form.resetFields();
            setIsActive(true);
            setAcceptDoc(false);
            setShowSecondaryLocale(false);
        }
    }, [open, initialValues, form, primaryLocale, secondaryLocale, clearPromptCooldownTimer]);

    useEffect(() => () => clearPromptCooldownTimer(), [clearPromptCooldownTimer]);

    const handleCancel = () => {
        if (!isSaving) {
            onClose();
        }
    };

    const handleOk = async () => {
        if (isSaving) return;

        try {
            const values = await form.validateFields();
            const payload = buildCriterionPayload(values, initialValues);
            if (isEditMode && !canEditPrompt) {
                delete payload.prompt;
            }
            if (!canEditReviewProfiles) {
                delete payload.review_profiles;
            }
            if (!payload.title) {
                message.warning(t("form.required"));
                return;
            }
            setIsSaving(true);
            await onSubmit(payload);
        } catch (info) {
            if (info?.errorFields) {
                message.warning(t("form.required"));
            } else {
                console.error("Criterion save failed:", info);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleGeneratePrompt = async () => {
        if (generationInFlightRef.current) return;
        if (promptCooldownUntil > Date.now()) {
            message.info(t("form.generationCoolingDown"));
            return;
        }

        const title = form.getFieldValue(primaryTitleName) || form.getFieldValue(secondaryTitleName);
        const description = form.getFieldValue(primaryDescriptionName) || form.getFieldValue(secondaryDescriptionName);
        
        if (!title) {
            return message.warning(t("form.fillTitleFirst"));
        }

        generationInFlightRef.current = true;
        setIsGenerating(true);
        form.setFieldsValue({ prompt: "" });
        dispatch(setLoading(true));
        
        dispatch(
            RequestGeneratePrompt(
                title,
                description,
                (partialPrompt) => form.setFieldsValue({ prompt: partialPrompt }),
                () => {
                    dispatch(setLoading(false));
                    generationInFlightRef.current = false;
                    setIsGenerating(false);
                    startPromptCooldown();
                    message.success(t("form.promptGenerated"));
                },
                () => {
                    dispatch(setLoading(false));
                    generationInFlightRef.current = false;
                    setIsGenerating(false);
                    startPromptCooldown();
                }
            )
        );
    };

    const handleActiveToggle = (checked) => {
        setIsActive(checked);
        form.setFieldsValue({ is_active: checked });
    };

    const handleDocToggle = (checked) => {
        setAcceptDoc(checked);
        form.setFieldsValue({ accept_document: checked });
    };

    const handleReviewStrategyChange = (strategy) => {
        if (strategy === 'reference_review') {
            setAcceptDoc(true);
            form.setFieldsValue({accept_document: true});
        }
    };

    const isEditMode = !!initialValues;
    const promptFieldDisabled = isEditMode && !canEditPrompt;
    const promptGenerationCoolingDown = promptCooldownUntil > Date.now();
    const promptGenerationDisabled = isGenerating || promptGenerationCoolingDown;
    const canChooseVisibility = currentUser?.role === "SUPER_ADMIN" || currentUser?.role === "ADMIN_CLIENT";
    const effectiveVisibilityScope = watchedVisibilityScope
        || initialValues?.visibility_scope
        || (currentUser?.role === "SUPER_ADMIN" ? "SYSTEM" : "CLIENT_SHARED");
    const showReviewProfilesField = currentUser?.role === "SUPER_ADMIN";
    const canEditReviewProfiles = showReviewProfilesField && effectiveVisibilityScope === "SYSTEM";
    const reviewProfileOptions = buildReviewProfileOptions(t);
    const visibilityOptions = currentUser?.role === "SUPER_ADMIN"
        ? [
            { value: "SYSTEM", label: t("form.visibilityOptions.verdictiaLibrary") },
            { value: "PRIVATE", label: t("form.visibilityOptions.personalWorkspace") },
        ]
        : [
            { value: "CLIENT_SHARED", label: t("form.visibilityOptions.teamLibrary") },
            { value: "PRIVATE", label: t("form.visibilityOptions.personalWorkspace") },
        ];
    return (
        <Modal
            title={
                <div className="criterion-modal__title">
                    <div className="criterion-modal__title-icon">
                        {isEditMode ? <EditOutlined /> : <PlusOutlined />}
                    </div>
                    <span>{isEditMode ? t("form.editTitle") : t("form.addTitle")}</span>
                </div>
            }
            open={open}
            onCancel={handleCancel}
            onOk={handleOk}
            confirmLoading={isSaving}
            okText={isEditMode ? t("form.update") : t("form.create")}
            okButtonProps={{ className: "verdictia-primary-btn", disabled: isGenerating }}
            cancelButtonProps={{ disabled: isSaving }}
            cancelText={t("actions.cancel")}
            centered
            width={980}
            zIndex={1050}
            className="criterion-modal criterion-modal--premium"
            closable={false}
            destroyOnClose
        >
            <Form 
                form={form} 
                layout="vertical" 
                name="criterion_form" 
                className="criterion-form"
                initialValues={{ is_active: true, accept_document: false }}
            >
                <div className="criterion-form__shell">
                    <section className="criterion-form__main" aria-label="Définition du critère">
                {/* ═══ ROW 1: Section + Titre ═══ */}
                <div className="criterion-form__row">
                    <Form.Item
                        name="section"
                        label={t("form.section")}
                        rules={[{ required: true, message: t("form.required") }]}
                        className="criterion-form__col"
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder={t("form.sectionPlaceholder")}
                            options={uniqueSections.map((s) => ({ label: s, value: s }))}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={null}
                            onSearch={(text) => {
                                if (text && !existingSections.includes(text)) {
                                    form.setFieldsValue({ section: text });
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        name={primaryTitleName}
                        label={t("form.title")}
                        className="criterion-form__col"
                    >
                        <Input placeholder={t("form.titlePlaceholder")} />
                    </Form.Item>
                </div>

                {/* ═══ ROW 2: Description ═══ */}
                <Form.Item 
                    name={primaryDescriptionName}
                    label={t("form.description")}
                >
                    <Input.TextArea
                        placeholder={t("form.descriptionPlaceholder")}
                        autoSize={{ minRows: 1, maxRows: 2 }}
                    />
                </Form.Item>

                {/* ═══ ROW 3: Prompt IA ═══ */}
                {!showSecondaryLocale && (
                    <Button
                        type="text"
                        size="small"
                        className="criterion-form__secondary-language-toggle"
                        onClick={() => setShowSecondaryLocale(true)}
                    >
                        + {secondaryLocale.toUpperCase()}
                    </Button>
                )}

                {showSecondaryLocale && (
                    <div className="criterion-form__secondary-language">
                        <div className="criterion-form__secondary-language-header">
                            {secondaryLocale.toUpperCase()}
                        </div>
                        <div className="criterion-form__row">
                            <Form.Item
                                name={secondaryTitleName}
                                label={t("form.title")}
                                className="criterion-form__col"
                            >
                                <Input placeholder={t("form.titlePlaceholder")} />
                            </Form.Item>

                            <Form.Item
                                name={secondaryDescriptionName}
                                label={t("form.description")}
                                className="criterion-form__col"
                            >
                                <Input.TextArea
                                    placeholder={t("form.descriptionPlaceholder")}
                                    autoSize={{ minRows: 1, maxRows: 2 }}
                                />
                            </Form.Item>
                        </div>
                    </div>
                )}

                <div className="criterion-form__ai-block">
                    <div className="criterion-form__ai-header">
                        <span className="criterion-form__ai-label">
                            <BulbOutlined className="criterion-form__ai-icon" />
                            {t("form.prompt")}
                        </span>
                        {canGeneratePrompt && (
                            <button
                                type="button"
                                className={`criterion-form__ai-btn verdictia-ai-magic-btn ${isGenerating ? 'criterion-form__ai-btn--loading' : ''}`}
                                onClick={handleGeneratePrompt}
                                disabled={promptGenerationDisabled}
                            >
                                {isGenerating ? (
                                    <span className="criterion-form__ai-dots"><span></span><span></span><span></span></span>
                                ) : (
                                    <>
                                        <ThunderboltOutlined />
                                        {t("form.generateAI")}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    <Form.Item
                        name="prompt"
                        rules={promptFieldDisabled ? [] : [{ required: true, message: t("form.requiredPrompt") }]}
                        className="criterion-form__item--no-margin"
                    >
                        <Input.TextArea
                            placeholder={t("form.promptPlaceholder")}
                            disabled={promptFieldDisabled}
                            autoSize={{ minRows: 9, maxRows: 16 }}
                        />
                    </Form.Item>
                </div>
                    </section>

                    <aside className="criterion-form__side" aria-label="Paramètres du critère">
                        <div className="criterion-form__side-header">
                            <span className="criterion-form__side-kicker">{t("form.settingsKicker")}</span>
                            <strong>{t("form.settingsTitle")}</strong>
                        </div>
                {/* ═══ ROW 4: Options inline ═══ */}
                <div className="criterion-form__options">
                    <Form.Item
                        name="criterion_type"
                        label={t("form.reviewStrategy")}
                        initialValue="focused_review"
                        className="criterion-form__visibility"
                    >
                        <Select
                            onChange={handleReviewStrategyChange}
                            options={[
                                {value: 'focused_review', label: t('form.reviewStrategyOptions.focused')},
                                {value: 'reference_review', label: t('form.reviewStrategyOptions.reference')},
                            ]}
                        />
                    </Form.Item>
                    {canChooseVisibility && (
                        <Form.Item
                            name="visibility_scope"
                            label={(
                                <span className="criterion-form__label-with-help">
                                    {t("form.visibility")}
                                    <Tooltip title={t("form.visibilityHelp")} placement="top">
                                        <InfoCircleOutlined className="criterion-form__help-icon" />
                                    </Tooltip>
                                </span>
                            )}
                            initialValue={currentUser?.role === "SUPER_ADMIN" ? "SYSTEM" : "CLIENT_SHARED"}
                            className="criterion-form__visibility"
                        >
                            <Select options={visibilityOptions} />
                        </Form.Item>
                    )}

                    {showReviewProfilesField && (
                        <Form.Item
                            name="review_profiles"
                            label={t("form.reviewProfiles")}
                            initialValue={[]}
                            extra={
                                canEditReviewProfiles
                                    ? t("form.reviewProfilesHelp")
                                    : t("form.reviewProfilesSystemOnly")
                            }
                            className="criterion-form__visibility"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                disabled={!canEditReviewProfiles}
                                placeholder={t("form.reviewProfilesPlaceholder")}
                                options={reviewProfileOptions}
                            />
                        </Form.Item>
                    )}

                    <div 
                        className={`criterion-form__option ${isActive ? 'criterion-form__option--active' : ''}`}
                        onClick={() => handleActiveToggle(!isActive)}
                    >
                            <CheckCircleOutlined className="criterion-form__option-icon" />
                            <span className="criterion-form__option-copy">
                                <span className="criterion-form__option-label">{t("form.active")}</span>
                            <small>{t("form.activeHintShort")}</small>
                        </span>
                        <div className="criterion-form__option-switch" onClick={(e) => e.stopPropagation()}>
                            <Form.Item name="is_active" valuePropName="checked" initialValue={true} noStyle>
                                <Switch checked={isActive} onChange={handleActiveToggle} size="small" />
                            </Form.Item>
                        </div>
                    </div>

                    <div 
                        className={`criterion-form__option criterion-form__option--doc ${acceptDoc ? 'criterion-form__option--active' : ''}`}
                        onClick={() => handleDocToggle(!acceptDoc)}
                    >
                            <FileTextOutlined className="criterion-form__option-icon" />
                            <span className="criterion-form__option-copy">
                                <span className="criterion-form__option-label">{t("form.acceptDocument")}</span>
                            <small>{t("form.acceptDocHintShort")}</small>
                        </span>
                        <div className="criterion-form__option-switch" onClick={(e) => e.stopPropagation()}>
                            <Form.Item name="accept_document" valuePropName="checked" initialValue={false} noStyle>
                                <Switch checked={acceptDoc} onChange={handleDocToggle} size="small" />
                            </Form.Item>
                        </div>
                    </div>

                </div>
                    </aside>
                </div>
            </Form>
        </Modal>
    );
};

export default CriterionFormModal;
